import axios from 'axios';
import HTTPClient from '@utils/httpClient';

import { deleteUserCookiesFromBrowser } from './users';
import { getRefreshToken, getToken, setToken, refreshAuth } from '@contexts/Token.context';

const DEPLOYMENT = import.meta.env.MODE;
const BASE_API_URL = import.meta.env.VITE_APP_BASE_API_URL;

const getBaseURL = () => {
	if (BASE_API_URL) {
		return BASE_API_URL;
	}
	return DEPLOYMENT === 'production'
		? 'https://api.biodroneportal.com/api'
		: 'http://localhost:8080/api';
};

export const baseURL = getBaseURL();

// V2 API response unwrapper for compatibility with useQuery
// V2 APIs return { success, message, data, errors }
// This helper extracts the data and handles errors
export const unwrapV2Response = (apiResponse ) => {
	if (!apiResponse?.success) {
		const error = new Error(apiResponse?.message || 'API request failed');
		error.apiErrors = apiResponse?.errors;
		throw error;
	}
	
	return apiResponse.data;
};

// New HTTPClient instances
export const apiClient = new HTTPClient(baseURL, {
	getToken,
	refreshAuth,
	retries: 3,
	backOffFactor: 1000,
});

export const publicApiClient = new HTTPClient(baseURL);

// Legacy axios instance (to be deprecated)
export const api = axios.create({
	baseURL,
});

api.interceptors.request.use(
	config => {
		const token = getToken();

		if (token) {
			config.headers = {
				...config.headers,
				Authorization: `Bearer ${token}`,
			};
		}

		return config;
	},
	error => {
		return Promise.reject(error);
	}
);

api.interceptors.response.use(
	response => response,
	async error => {
		console.log('We have an intercept');
		console.log({ response: error.response });

		const originalRequest = error.config;

		if (error?.response?.status === 401 && !originalRequest.sent) {
			const originalAccessToken = getToken();
			const refreshToken = getRefreshToken();

			if (!refreshToken || !originalAccessToken) {
				console.warn('No access or refresh token found in cookies!');

				deleteUserCookiesFromBrowser();

				return Promise.reject(error);
			}

			const response = await axios
				.post(baseURL + '/auth/refresh', {
					access_token: originalAccessToken,
					refresh_token: refreshToken,
				})
				.then(res => res)
				.catch(err => err);

			if (response.status === 200) {
				const newAccessToken = response.data.token ?? null;

				if (!newAccessToken) {
					console.error(
						'Did not receive a new access token from the refresh token request!',
						response
					);

					deleteUserCookiesFromBrowser();

					return Promise.reject(error);
				}

				originalRequest.sent = true;
				originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

				setToken(newAccessToken);

				return api(originalRequest);
			} else {
				deleteUserCookiesFromBrowser();

				// Check if the current URL is already the home url
				const currentURL = window.location.pathname;
				const homeURL = '/';

				if (currentURL !== homeURL) {
					window.location = homeURL;
				}
			}
		}
		console.log('We have an error that we are just passing back as a promise rejection');
		return Promise.reject(error);
	}
);

export * from './helpers';
export * from './auth';
export * from './biodroneai';
export * from './jobs';
export * from './projects';
export * from './training';
export * from './tasks';
export * from './users';
export * from './organizations';
export * from './admin';
export * from './models';
export * from './stripe';
export * from './files';
export * from './filterTemplates';
export * from './subscriptions';
export * from './notifications';
export * from './evaluation';
export * from './grid';
export * from './annotations';
export * from './maptiler';
